import React, { useState, useEffect, useRef } from "react";
import api from "../api";
import "./style/SocialGallery.css";

/**
 * @mention autocomplete input for tagging users.
 *
 * Props:
 *   eventId       number
 *   value         string  — current input value
 *   onChange      (value: string) => void
 *   onSelect      (user: {user_id, display_name}) => void
 *   placeholder   string
 */
export default function TagAutocomplete({
  eventId,
  value,
  onChange,
  onSelect,
  placeholder = "Tag someone (@name)",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Detect @mention trigger
    const match = value.match(/@(\w*)$/);
    if (!match) {
      setOpen(false);
      setSuggestions([]);
      return;
    }
    const query = match[1];

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get("/social/taggable", {
          params: { eid: eventId, q: query },
        });
        if (res.data.success && res.data.suggestions.length > 0) {
          setSuggestions(res.data.suggestions);
          setOpen(true);
        } else {
          setOpen(false);
        }
      } catch {
        setOpen(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [value, eventId]);

  function handleSelect(user) {
    // Replace the @partial with @display_name
    const newValue = value.replace(/@(\w*)$/, `@${user.display_name} `);
    onChange(newValue);
    onSelect(user);
    setOpen(false);
    setSuggestions([]);
  }

  return (
    <div className="tag-autocomplete">
      <input
        type="text"
        className="form-control form-control-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {open && suggestions.length > 0 && (
        <div className="tag-autocomplete__dropdown">
          {suggestions.map((s) => (
            <div
              key={s.user_id}
              className="tag-autocomplete__item"
              onMouseDown={() => handleSelect(s)}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "#dee2e6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#495057",
                  flexShrink: 0,
                }}
              >
                {s.display_name.charAt(0).toUpperCase()}
              </div>
              <span>{s.display_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
